import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useMonth } from "../context/MonthContext";
import { 
  FloppyDisk, ArrowsClockwise, Check, Warning, 
  CloudArrowUp, PencilSimple, Eye
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = {
  get: (url) => axios.get(`${API}${url}`),
  put: (url, data) => axios.put(`${API}${url}`, data),
  post: (url, data) => axios.post(`${API}${url}`, data),
};

const formatCurrency = (amount, currency = "INR") => {
  if (amount === 0 || !amount) return "-";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const AdSpendEntry = () => {
  const { selectedMonth, monthLabel } = useMonth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editedRows, setEditedRows] = useState(new Set());

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/ad-spend/${selectedMonth}?include_inactive=${includeInactive}`);
      setEntries(res.data.entries || []);
      setEditedRows(new Set());
    } catch (err) {
      console.error("Failed to fetch ad spend data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth, includeInactive]);

  const handleCellChange = (clientId, field, value) => {
    const numValue = parseFloat(value) || 0;
    
    setEntries(prev => prev.map(entry => {
      if (entry.client_id === clientId) {
        const updated = { ...entry, [field]: numValue };
        // Recalculate totals
        updated.total_ad_spend = (updated.meta_spend || 0) + (updated.google_spend || 0) + (updated.other_spend || 0);
        updated.roi = updated.total_ad_spend > 0 ? updated.revenue / updated.total_ad_spend : 0;
        return updated;
      }
      return entry;
    }));
    
    setEditedRows(prev => new Set([...prev, clientId]));
  };

  const saveRow = async (clientId) => {
    const entry = entries.find(e => e.client_id === clientId);
    if (!entry) return;
    
    try {
      await api.put(`/ad-spend/${selectedMonth}/${clientId}`, {
        meta_spend: entry.meta_spend,
        google_spend: entry.google_spend,
        other_spend: entry.other_spend
      });
      
      setEditedRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(clientId);
        return newSet;
      });
      
      // Update status locally
      setEntries(prev => prev.map(e => 
        e.client_id === clientId ? { ...e, status: entry.total_ad_spend > 0 ? "completed" : "pending" } : e
      ));
    } catch (err) {
      console.error("Failed to save:", err);
      alert("Failed to save entry");
    }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const entriesToSave = entries.filter(e => editedRows.has(e.client_id));
      await api.post(`/ad-spend/${selectedMonth}/save-all`, entriesToSave);
      setEditedRows(new Set());
      alert(`Saved ${entriesToSave.length} entries`);
      fetchData();
    } catch (err) {
      console.error("Failed to save all:", err);
      alert("Failed to save entries");
    } finally {
      setSaving(false);
    }
  };

  const syncAllRevenue = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/dashboard/sync-all-revenue?month=${selectedMonth}`);
      alert(`Synced ${res.data.synced} clients successfully!`);
      fetchData();
    } catch (err) {
      console.error("Failed to sync:", err);
      alert("Failed to sync revenue data");
    } finally {
      setSyncing(false);
    }
  };

  const getStatusBadge = (entry) => {
    if (entry.manual_override_active) {
      return <span className="badge" style={{ background: '#3B82F6', color: 'white' }}>MANUAL OVERRIDE</span>;
    }
    if (entry.facebook_synced) {
      return <span className="badge" style={{ background: '#1877F2', color: 'white' }} data-testid="fb-synced-badge">FB SYNCED</span>;
    }
    if (entry.shopify_synced) {
      return <span className="badge badge-active">SHOPIFY SYNCED</span>;
    }
    if (entry.status === "completed") {
      return <span className="badge badge-final">COMPLETED</span>;
    }
    return <span className="badge badge-draft">PENDING</span>;
  };

  const getRoiBadge = (roi, targetRoas = 3.0) => {
    if (roi === 0) return <span className="text-[#71717A]">-</span>;
    if (roi >= targetRoas) return <span style={{ color: '#00C853', fontWeight: 600 }}>{roi.toFixed(2)}x</span>;
    if (roi >= targetRoas * 0.7) return <span style={{ color: '#FFEB00', fontWeight: 600 }}>{roi.toFixed(2)}x</span>;
    return <span style={{ color: '#FF4444', fontWeight: 600 }}>{roi.toFixed(2)}x</span>;
  };

  return (
    <div data-testid="ad-spend-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Ad Spend Entry</h1>
          <p className="text-[#A1A1AA] mt-1">{monthLabel} - Enter ad spend for each client</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={syncAllRevenue} 
            className="btn-secondary flex items-center gap-2"
            disabled={syncing}
          >
            <CloudArrowUp size={18} className={syncing ? "animate-pulse" : ""} />
            {syncing ? "Syncing..." : "Sync Revenue"}
          </button>
          {editedRows.size > 0 && (
            <button 
              onClick={saveAll} 
              className="btn-primary flex items-center gap-2"
              disabled={saving}
            >
              <FloppyDisk size={18} />
              {saving ? "Saving..." : `Save All (${editedRows.size})`}
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="w-4 h-4"
          />
          Include inactive clients
        </label>
        <span className="text-sm text-[#71717A]">
          {entries.length} clients • {entries.filter(e => e.status === "completed").length} completed
        </span>
      </div>

      <div className="card-flat overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#71717A]">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-dense" style={{ minWidth: '1200px' }}>
              <thead>
                <tr>
                  <th style={{ width: '200px' }}>Client</th>
                  <th style={{ width: '140px' }}>Project</th>
                  <th className="text-right" style={{ width: '130px' }}>Meta/FB Spend</th>
                  <th className="text-right" style={{ width: '130px' }}>Google Spend</th>
                  <th className="text-right" style={{ width: '130px' }}>Other Spend</th>
                  <th className="text-right" style={{ width: '130px' }}>Total Ad Spend</th>
                  <th className="text-right" style={{ width: '130px' }}>Revenue</th>
                  <th className="text-right" style={{ width: '80px' }}>ROI</th>
                  <th className="text-right" style={{ width: '70px' }}>Target</th>
                  <th style={{ width: '150px' }}>Status</th>
                  <th style={{ width: '80px' }}></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr 
                    key={entry.client_id}
                    className={editedRows.has(entry.client_id) ? "bg-[#1A1A00]" : ""}
                  >
                    <td className="font-medium">
                      <Link
                        to={`/clients/${entry.client_id}`}
                        className="hover:text-[#FFEB00] transition-colors"
                        data-testid={`adspend-client-link-${entry.client_id}`}
                      >
                        {entry.client_name}
                      </Link>
                    </td>
                    <td className="text-[#A1A1AA] text-sm">{entry.project_name}</td>
                    <td className="text-right p-1">
                      <input
                        type="number"
                        className="input-default w-full text-right"
                        value={entry.meta_spend || ""}
                        onChange={(e) => handleCellChange(entry.client_id, "meta_spend", e.target.value)}
                        placeholder="0"
                      />
                    </td>
                    <td className="text-right p-1">
                      <input
                        type="number"
                        className="input-default w-full text-right"
                        value={entry.google_spend || ""}
                        onChange={(e) => handleCellChange(entry.client_id, "google_spend", e.target.value)}
                        placeholder="0"
                      />
                    </td>
                    <td className="text-right p-1">
                      <input
                        type="number"
                        className="input-default w-full text-right"
                        value={entry.other_spend || ""}
                        onChange={(e) => handleCellChange(entry.client_id, "other_spend", e.target.value)}
                        placeholder="0"
                      />
                    </td>
                    <td className="text-right data-number font-semibold">
                      {formatCurrency(entry.total_ad_spend, entry.currency)}
                    </td>
                    <td className="text-right data-number">
                      <div className="flex items-center justify-end gap-1">
                        {formatCurrency(entry.revenue, entry.currency)}
                        {entry.manual_override_active && (
                          <PencilSimple size={12} className="text-[#3B82F6]" />
                        )}
                      </div>
                    </td>
                    <td className="text-right">{getRoiBadge(entry.roi, entry.target_roas)}</td>
                    <td className="text-right text-[#71717A] text-xs">{entry.target_roas || 3.0}x</td>
                    <td>{getStatusBadge(entry)}</td>
                    <td className="text-center">
                      {editedRows.has(entry.client_id) && (
                        <button
                          onClick={() => saveRow(entry.client_id)}
                          className="btn-ghost p-1"
                          title="Save"
                        >
                          <Check size={18} style={{ color: '#00C853' }} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4 p-4 card-flat">
        <h3 className="text-sm font-semibold mb-2 text-[#A1A1AA]">Legend</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <span className="flex items-center gap-2">
            <span className="badge badge-active text-xs">SHOPIFY SYNCED</span> Revenue from Shopify
          </span>
          <span className="flex items-center gap-2">
            <span className="badge text-xs" style={{ background: '#1877F2', color: 'white' }}>FB SYNCED</span> Meta spend pulled from Facebook
          </span>
          <span className="flex items-center gap-2">
            <span className="badge text-xs" style={{ background: '#3B82F6', color: 'white' }}>MANUAL OVERRIDE</span> Manual revenue entry
          </span>
          <span className="flex items-center gap-2">
            <span className="badge badge-draft text-xs">PENDING</span> Awaiting ad spend entry
          </span>
          <span className="flex items-center gap-2">
            <span className="badge badge-final text-xs">COMPLETED</span> Ad spend entered
          </span>
        </div>
      </div>
    </div>
  );
};

export default AdSpendEntry;
